import React from 'react';
import { H5, H6 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import { getFieldAccess } from '../../../../_helper/fieldAccess';
import { isNoInsuranceProvider, shouldRequirePolicyDetails } from '../../../../_helper/mandatoryFieldRules';
import { validateTaxNumber, validateTcid, normalizeFieldValue, REGEX, validateLicensePlate, displayDecimal } from '../../../../_helper/regexHandler';

const DocumentRequestForm = ({ formData, errors, setFormData, handleInputChange, handleCounterPartyTypeChange, insuranceCompanies, fieldPermissions = [], canEdit = true }) => {
    const isOpenDamageFileAccess = getFieldAccess(fieldPermissions, 'licenseplates.isthereopendamagefile', canEdit);
    const docRequestDateAccess = getFieldAccess(fieldPermissions, 'accidents.documentrequestdate', canEdit);
    const docReceivedDateAccess = getFieldAccess(fieldPermissions, 'accidents.documentreceiveddate', canEdit);
    const docRequestStatusAccess = getFieldAccess(fieldPermissions, 'accidents.documentrequeststatus', canEdit);
    const ourFaultRateAccess = getFieldAccess(fieldPermissions, 'accidents.ourfaultrate', canEdit);
    const counterpartyFaultRateAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartyfaultrate', canEdit);
    const counterpartyTypeAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartytype', canEdit);
    const counterpartyIdAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartyidentificationnumber', canEdit);
    const counterpartyTaxAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartytaxnumber', canEdit);
    const counterpartyOwnerAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartycarowner', canEdit);
    const counterpartyPlateAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartylicenseplatenumber', canEdit);
    const counterpartyAddressAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartyaddress', canEdit);
    const clientTrafficAccess = getFieldAccess(fieldPermissions, 'licenseplates.trafficinsurancepolicy', canEdit);
    const clientCascoAccess = getFieldAccess(fieldPermissions, 'licenseplates.cascoinsurancepolicy', canEdit);
    const cpTrafficAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartytrafficinsurancepolicy', canEdit);
    const cpCascoAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartycascoinsurancepolicy', canEdit);
    const clientTrafficStartAccess = getFieldAccess(fieldPermissions, 'licenseplates.trafficinsurancepolicy.trafficinsurancestart', canEdit);
    const clientCascoStartAccess = getFieldAccess(fieldPermissions, 'licenseplates.cascoinsurancepolicy.cascoinsurancestart', canEdit);
    const cpTrafficStartAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartytrafficinsurancepolicy.trafficinsurancestart', canEdit);
    const cpCascoStartAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartycascoinsurancepolicy.cascoinsurancestart', canEdit);
    const clientTrafficDetailsRequired = clientTrafficAccess.required && shouldRequirePolicyDetails(formData.trafficInsurancePolicy, insuranceCompanies);
    const clientCascoDetailsRequired = clientCascoAccess.required && shouldRequirePolicyDetails(formData.cascoInsurancePolicy, insuranceCompanies, 'cascoInsuranceProvider');
    const cpTrafficDetailsRequired = cpTrafficAccess.required && shouldRequirePolicyDetails(formData.counterpartyTrafficInsurancePolicy, insuranceCompanies);
    const cpCascoDetailsRequired = cpCascoAccess.required && shouldRequirePolicyDetails(formData.counterpartyCascoInsurancePolicy, insuranceCompanies, 'cascoInsuranceProvider');
    const clientTrafficIsNoInsurance = isNoInsuranceProvider(formData.trafficInsurancePolicy?.trafficInsuranceProvider?.[0], insuranceCompanies);
    const clientCascoIsNoInsurance = isNoInsuranceProvider(formData.cascoInsurancePolicy?.cascoInsuranceProvider?.[0], insuranceCompanies);
    const cpTrafficIsNoInsurance = isNoInsuranceProvider(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceProvider?.[0], insuranceCompanies);
    const cpCascoIsNoInsurance = isNoInsuranceProvider(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceProvider?.[0], insuranceCompanies);
    const documentReceivedDateRequired = docReceivedDateAccess.required && formData.documentRequestStatus === 'Evraklar Geldi';

    const handleClientTrafficInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        const isNoInsurance = isNoInsuranceProvider(providerId, insuranceCompanies);
        handleInputChange({
            target: {
                name: 'trafficInsurancePolicy',
                value: {
                    ...formData.trafficInsurancePolicy,
                    trafficInsuranceProvider: providerId === "" ? [] : [providerId],
                    ...(isNoInsurance ? {
                        trafficInsuranceNumber: '',
                        trafficInsuranceStart: null,
                        trafficInsuranceExpiry: null,
                    } : {})
                }
            }
        });
    };

    const handleClientCascoInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        const isNoInsurance = isNoInsuranceProvider(providerId, insuranceCompanies);
        handleInputChange({
            target: {
                name: 'cascoInsurancePolicy',
                value: {
                    ...formData.cascoInsurancePolicy,
                    cascoInsuranceProvider: providerId === "" ? [] : [providerId],
                    ...(isNoInsurance ? {
                        cascoInsuranceNumber: '',
                        cascoInsuranceStart: null,
                        cascoInsuranceExpiry: null,
                    } : {})
                }
            }
        });
    };

    const handleCounterpartyTrafficInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        const isNoInsurance = isNoInsuranceProvider(providerId, insuranceCompanies);
        handleInputChange({
            target: {
                name: 'counterpartyTrafficInsurancePolicy',
                value: {
                    ...formData.counterpartyTrafficInsurancePolicy,
                    trafficInsuranceProvider: providerId === "" ? [] : [providerId],
                    ...(isNoInsurance ? {
                        trafficInsuranceNumber: '',
                        trafficInsuranceStart: null,
                        trafficInsuranceExpiry: null,
                    } : {})
                }
            }
        });
    };

    const handleCounterpartyCascoInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        const isNoInsurance = isNoInsuranceProvider(providerId, insuranceCompanies);
        handleInputChange({
            target: {
                name: 'counterpartyCascoInsurancePolicy',
                value: {
                    ...formData.counterpartyCascoInsurancePolicy,
                    cascoInsuranceProvider: providerId === "" ? [] : [providerId],
                    ...(isNoInsurance ? {
                        cascoInsuranceNumber: '',
                        cascoInsuranceStart: null,
                        cascoInsuranceExpiry: null,
                    } : {})
                }
            }
        });
    };

    const handleClientTrafficInsuranceNumberChange = (e) => {
        const normalizedValue = normalizeFieldValue('trafficInsuranceNumber', e.target.value);
        handleInputChange({
            target: {
                name: 'trafficInsurancePolicy',
                value: {
                    ...formData.trafficInsurancePolicy,
                    trafficInsuranceNumber: normalizedValue
                }
            }
        });
    };

    const handleClientCascoInsuranceNumberChange = (e) => {
        const normalizedValue = normalizeFieldValue('cascoInsuranceNumber', e.target.value);
        handleInputChange({
            target: {
                name: 'cascoInsurancePolicy',
                value: {
                    ...formData.cascoInsurancePolicy,
                    cascoInsuranceNumber: normalizedValue
                }
            }
        });
    };

    const handleCounterpartyTrafficInsuranceNumberChange = (e) => {
        const normalizedValue = normalizeFieldValue('counterpartyTrafficInsuranceNumber', e.target.value);
        handleInputChange({
            target: {
                name: 'counterpartyTrafficInsurancePolicy',
                value: {
                    ...formData.counterpartyTrafficInsurancePolicy,
                    trafficInsuranceNumber: normalizedValue
                }
            }
        });
    };

    const handleCounterpartyCascoInsuranceNumberChange = (e) => {
        const normalizedValue = normalizeFieldValue('counterpartyCascoInsuranceNumber', e.target.value);
        handleInputChange({
            target: {
                name: 'counterpartyCascoInsurancePolicy',
                value: {
                    ...formData.counterpartyCascoInsurancePolicy,
                    cascoInsuranceNumber: normalizedValue
                }
            }
        });
    };

    return (
        <>
            <H5>Evrak Talep Bilgileri</H5>
            <div style={{ height: "20px" }}></div>

            <FormGroup className="d-flex align-items-center mb-3">
                <Label for="isThereOpenDamageFileRadio" className="me-3 text-nowrap" style={{ minWidth: '200px' }}>
                    Açık Hasar Dosyası Var Mı? {isOpenDamageFileAccess.required && <span className="text-danger">*</span>}
                </Label>
                <div className="d-flex">
                    <div className="radio radio-success me-3 d-flex align-items-center">
                        <Input
                            id="isThereOpenDamageFileTrue"
                            type="radio"
                            name="isThereOpenDamageFile"
                            value="yes"
                            checked={formData.isThereOpenDamageFile === "yes"}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isThereOpenDamageFile: "yes",
                                }))
                            }
                            invalid={!!errors.isThereOpenDamageFile}
                            disabled={!isOpenDamageFileAccess.editable}
                            required={isOpenDamageFileAccess.required}
                        />
                        <Label for="isThereOpenDamageFileTrue" className="ms-1 mb-0">
                            Evet
                        </Label>
                    </div>
                    <div className="radio radio-danger d-flex align-items-center">
                        <Input
                            id="isThereOpenDamageFileFalse"
                            type="radio"
                            name="isThereOpenDamageFile"
                            value="no"
                            checked={formData.isThereOpenDamageFile === "no"}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isThereOpenDamageFile: "no",
                                }))
                            }
                            invalid={!!errors.isThereOpenDamageFile}
                            disabled={!isOpenDamageFileAccess.editable}
                        />
                        <Label for="isThereOpenDamageFileFalse" className="ms-1 mb-0">
                            Hayır
                        </Label>
                    </div>
                    <FormFeedback>{errors.isThereOpenDamageFile}</FormFeedback>
                </div>
            </FormGroup>

            <FormGroup>
                <Label for="documentRequestDate">Evrak Talep Edilme Tarihi {docRequestDateAccess.required && <span className="text-danger">*</span>}</Label>
                <Input
                    type="date"
                    id="documentRequestDate"
                    name="documentRequestDate"
                    value={formData.documentRequestDate instanceof Date && !isNaN(formData.documentRequestDate)
                        ? formData.documentRequestDate.toISOString().substring(0, 10)
                        : ''}
                    onChange={(e) => {
                        const value = e.target.value ? new Date(e.target.value) : null;
                        handleInputChange({
                            target: {
                                name: 'documentRequestDate',
                                value
                            }
                        });
                    }}
                    invalid={!!errors.documentRequestDate}
                    disabled={!docRequestDateAccess.editable}
                    required={docRequestDateAccess.required}
                />
                <FormFeedback>{errors.documentRequestDate}</FormFeedback>
            </FormGroup>

            <FormGroup>
                <Label for="documentRequestStatus">Evrak Talep Durumu</Label>
                <Input
                    type="text"
                    id="documentRequestStatus"
                    name="documentRequestStatus"
                    value={formData.documentRequestStatus}
                    onChange={handleInputChange}
                    disabled={!docRequestStatusAccess.editable}
                    invalid={!!errors.documentRequestStatus}
                />
                <FormFeedback>{errors.documentRequestStatus}</FormFeedback>
            </FormGroup>

            <FormGroup>
                <Label for="documentReceivedDate">Evrak Gelme Tarihi {documentReceivedDateRequired && <span className="text-danger">*</span>}</Label>
                <Input
                    type="date"
                    id="documentReceivedDate"
                    name="documentReceivedDate"
                    value={formData.documentReceivedDate instanceof Date && !isNaN(formData.documentReceivedDate)
                        ? formData.documentReceivedDate.toISOString().substring(0, 10)
                        : formData.documentReceivedDate || ''}
                    onChange={(e) => {
                        const value = e.target.value ? new Date(e.target.value) : null;
                        handleInputChange({
                            target: {
                                name: 'documentReceivedDate',
                                value
                            }
                        });
                    }}
                    invalid={!!errors.documentReceivedDate}
                    disabled={!docReceivedDateAccess.editable}
                    required={documentReceivedDateRequired}
                />
                <FormFeedback>{errors.documentReceivedDate}</FormFeedback>
            </FormGroup>

            <div style={{ height: "20px" }}></div>
            <hr className="mt-4 mb-4" />
            <div style={{ height: "20px" }}></div>

            <H6>Müvekkil Sigorta Bilgileri</H6>
            <div style={{ height: "20px" }}></div>

            <FormGroup>
                <Label for="ourFaultRate">Müvekkil Kusur Oranı {ourFaultRateAccess.required && <span className="text-danger">*</span>}</Label>
                <div className="input-group">
                    <Input
                        type="text"
                        id="ourFaultRate"
                        name="ourFaultRate"
                        value={displayDecimal(formData.ourFaultRate || '')}
                        onChange={(e) => {
                            const value = normalizeFieldValue('ourFaultRate', e.target.value);

                            handleInputChange({
                                target: {
                                    name: 'ourFaultRate',
                                    value
                                }
                            });

                            const counterValue = value ? (100 - parseFloat(value)).toString() : '';
                            handleInputChange({
                                target: {
                                    name: 'counterpartyFaultRate',
                                    value: counterValue
                                }
                            });
                        }}
                        placeholder="0-100"
                        invalid={!!errors.ourFaultRate || (formData.ourFaultRate && (parseFloat(formData.ourFaultRate) > 100 || parseFloat(formData.ourFaultRate) < 0))}
                        disabled={!ourFaultRateAccess.editable}
                        required={ourFaultRateAccess.required}
                    />
                    <div className="input-group-append">
                        <span className="input-group-text">%</span>
                    </div>
                    <FormFeedback>
                        {errors.ourFaultRate || 'Oran 0-100 arasında olmalıdır'}
                    </FormFeedback>
                </div>
            </FormGroup>

            <FormGroup>
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label">Müvekkil Trafik Sigorta Sağlayıcısı {clientTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="trafficInsuranceProvider"
                                name="trafficInsuranceProvider"
                                value={formData.trafficInsurancePolicy?.trafficInsuranceProvider?.[0] || ""}
                                onChange={handleClientTrafficInsuranceProviderChange}
                                invalid={!!errors.trafficInsuranceProvider}
                                disabled={!clientTrafficAccess.editable}
                                required={clientTrafficAccess.required}
                            >
                                <option value="">Sigorta Şirketi Seçiniz</option>
                                {insuranceCompanies && insuranceCompanies.length > 0 ?
                                    insuranceCompanies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    )) :
                                    <option value="" disabled>Sigorta şirketi bulunamadı</option>
                                }
                            </Input>
                            <FormFeedback>{errors.trafficInsuranceProvider}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <Label for="trafficInsuranceNumber">Müvekkil Trafik Sigorta Numarası {clientTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="trafficInsuranceNumber"
                            name="trafficInsuranceNumber"
                            value={formData.trafficInsurancePolicy?.trafficInsuranceNumber || ''}
                            onChange={handleClientTrafficInsuranceNumberChange}
                            invalid={!!errors.trafficInsuranceNumber}
                            disabled={!clientTrafficAccess.editable || clientTrafficIsNoInsurance}
                            required={clientTrafficDetailsRequired}
                        />
                        <FormFeedback>{errors.trafficInsuranceNumber}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="trafficInsuranceStart">Müvekkil Trafik Başlangıç {clientTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="trafficInsuranceStart"
                            name="trafficInsuranceStart"
                            value={formData.trafficInsurancePolicy?.trafficInsuranceStart instanceof Date && !isNaN(formData.trafficInsurancePolicy?.trafficInsuranceStart)
                                ? formData.trafficInsurancePolicy?.trafficInsuranceStart.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'trafficInsurancePolicy',
                                        value: {
                                            ...formData.trafficInsurancePolicy,
                                            trafficInsuranceStart: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.trafficInsuranceStart}
                            disabled={!clientTrafficStartAccess.editable || clientTrafficIsNoInsurance}
                            required={clientTrafficDetailsRequired}
                        />
                        <FormFeedback>{errors.trafficInsuranceStart}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="trafficInsuranceExpiry">Müvekkil Trafik Sigorta Vade {clientTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="trafficInsuranceExpiry"
                            name="trafficInsuranceExpiry"
                            value={formData.trafficInsurancePolicy?.trafficInsuranceExpiry instanceof Date && !isNaN(formData.trafficInsurancePolicy?.trafficInsuranceExpiry)
                                ? formData.trafficInsurancePolicy?.trafficInsuranceExpiry.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'trafficInsurancePolicy',
                                        value: {
                                            ...formData.trafficInsurancePolicy,
                                            trafficInsuranceExpiry: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.trafficInsuranceExpiry}
                            disabled={!clientTrafficAccess.editable || clientTrafficIsNoInsurance}
                            required={clientTrafficDetailsRequired}
                        />
                        <FormFeedback>{errors.trafficInsuranceExpiry}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <FormGroup>
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label">Müvekkil Kasko Sağlayıcısı {clientCascoAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="cascoInsuranceProvider"
                                name="cascoInsuranceProvider"
                                value={formData.cascoInsurancePolicy?.cascoInsuranceProvider?.[0] || ""}
                                onChange={handleClientCascoInsuranceProviderChange}
                                invalid={!!errors.cascoInsuranceProvider}
                                disabled={!clientCascoAccess.editable}
                                required={clientCascoAccess.required}
                            >
                                <option value="">Sigorta Şirketi Seçiniz</option>
                                {insuranceCompanies && insuranceCompanies.length > 0 ?
                                    insuranceCompanies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    )) :
                                    <option value="" disabled>Sigorta şirketi bulunamadı</option>
                                }
                            </Input>
                            <FormFeedback>{errors.cascoInsuranceProvider}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <Label for="cascoInsuranceNumber">Müvekkil Kasko Numarası {clientCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="cascoInsuranceNumber"
                            name="cascoInsuranceNumber"
                            value={formData.cascoInsurancePolicy?.cascoInsuranceNumber || ''}
                            onChange={handleClientCascoInsuranceNumberChange}
                            invalid={!!errors.cascoInsuranceNumber}
                            disabled={!clientCascoAccess.editable || clientCascoIsNoInsurance}
                            required={clientCascoDetailsRequired}
                        />
                        <FormFeedback>{errors.cascoInsuranceNumber}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="cascoInsuranceStart">Müvekkil Kasko Başlangıç {clientCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="cascoInsuranceStart"
                            name="cascoInsuranceStart"
                            value={formData.cascoInsurancePolicy?.cascoInsuranceStart instanceof Date && !isNaN(formData.cascoInsurancePolicy?.cascoInsuranceStart)
                                ? formData.cascoInsurancePolicy?.cascoInsuranceStart.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'cascoInsurancePolicy',
                                        value: {
                                            ...formData.cascoInsurancePolicy,
                                            cascoInsuranceStart: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.cascoInsuranceStart}
                            disabled={!clientCascoStartAccess.editable || clientCascoIsNoInsurance}
                            required={clientCascoDetailsRequired}
                        />
                        <FormFeedback>{errors.cascoInsuranceStart}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="cascoInsuranceExpiry">Müvekkil Kasko Vade {clientCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="cascoInsuranceExpiry"
                            name="cascoInsuranceExpiry"
                            value={formData.cascoInsurancePolicy?.cascoInsuranceExpiry instanceof Date && !isNaN(formData.cascoInsurancePolicy?.cascoInsuranceExpiry)
                                ? formData.cascoInsurancePolicy?.cascoInsuranceExpiry.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'cascoInsurancePolicy',
                                        value: {
                                            ...formData.cascoInsurancePolicy,
                                            cascoInsuranceExpiry: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.cascoInsuranceExpiry}
                            disabled={!clientCascoAccess.editable || clientCascoIsNoInsurance}
                            required={clientCascoDetailsRequired}
                        />
                        <FormFeedback>{errors.cascoInsuranceExpiry}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <div style={{ height: "20px" }}></div>
            <H6>Karşı Taraf Sigorta Bilgileri</H6>
            <div style={{ height: "20px" }}></div>

            <Row form>
                <Col md={6}>
                    <FormGroup>
                        <Label for="counterpartyType">Karşı Taraf Türü {counterpartyTypeAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="select"
                            id="counterpartyType"
                            name="counterpartyType"
                            value={formData.counterpartyType}
                            onChange={handleCounterPartyTypeChange}
                            className="form-control"
                            disabled={!counterpartyTypeAccess.editable}
                            required={counterpartyTypeAccess.required}
                        >
                            <option value="Bireysel">Bireysel</option>
                            <option value="Kurumsal">Kurumsal</option>
                        </Input>
                    </FormGroup>
                </Col>
                {formData.counterpartyType === 'Bireysel' && (
                    <Col md={6}>
                        <FormGroup>
                            <Label for="counterpartyIdentificationNumber">Karşı Taraf TC Kimlik Numarası {counterpartyIdAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="text"
                                id="counterpartyIdentificationNumber"
                                name="counterpartyIdentificationNumber"
                                value={formData.counterpartyIdentificationNumber}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('counterpartyIdentificationNumber', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'counterpartyIdentificationNumber',
                                            value
                                        }
                                    });
                                }}
                                maxLength={11}
                                placeholder="11 Haneli TC Kimlik No"
                                invalid={!!errors.counterpartyIdentificationNumber || (formData.counterpartyIdentificationNumber && !validateTcid(formData.counterpartyIdentificationNumber))}
                                disabled={!counterpartyIdAccess.editable}
                                required={counterpartyIdAccess.required}
                            />
                            <FormFeedback>
                                {errors.counterpartyIdentificationNumber || 'TC Kimlik Numarası 11 haneli olmalıdır'}
                            </FormFeedback>
                        </FormGroup>
                    </Col>
                )}
                {formData.counterpartyType === 'Kurumsal' && (
                    <Col md={6}>
                        <FormGroup>
                            <Label for="counterpartyTaxNumber">Karşı Taraf Vergi Numarası {counterpartyTaxAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="text"
                                id="counterpartyTaxNumber"
                                name="counterpartyTaxNumber"
                                value={formData.counterpartyTaxNumber}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('counterpartyTaxNumber', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'counterpartyTaxNumber',
                                            value
                                        }
                                    });
                                }}
                                maxLength={10}
                                invalid={!!errors.counterpartyTaxNumber || (formData.counterpartyTaxNumber && !validateTaxNumber(formData.counterpartyTaxNumber))}
                                disabled={!counterpartyTaxAccess.editable}
                                required={counterpartyTaxAccess.required}
                            />
                            <FormFeedback>{errors.counterpartyTaxNumber || 'Vergi Numarası 10 haneli olmalıdır'}</FormFeedback>
                        </FormGroup>
                    </Col>
                )}
            </Row>

            <Row form>
                <Col md={6}>
                    <FormGroup>
                        <Label for="counterpartyCarOwner">Karşı Taraf Araç Sahibi {counterpartyOwnerAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="counterpartyCarOwner"
                            name="counterpartyCarOwner"
                            value={formData.counterpartyCarOwner}
                            onChange={(e) => {
                                const value = normalizeFieldValue('counterpartyCarOwner', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyCarOwner',
                                        value
                                    }
                                });
                            }}
                            placeholder="Ad Soyad"
                            invalid={!!errors.counterpartyCarOwner || (formData.counterpartyCarOwner &&
                                !REGEX.FULL_NAME_TR.test(formData.counterpartyCarOwner))}
                            disabled={!counterpartyOwnerAccess.editable}
                            required={counterpartyOwnerAccess.required}
                        />
                        <FormFeedback>
                            {errors.counterpartyCarOwner || "Lütfen geçerli bir ad soyad giriniz (ör: Ahmet Yılmaz)"}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="counterpartyLicensePlateNumber">Karşı Taraf Plaka {counterpartyPlateAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="counterpartyLicensePlateNumber"
                            name="counterpartyLicensePlateNumber"
                            value={formData.counterpartyLicensePlateNumber}
                            onChange={(e) => {
                                const value = normalizeFieldValue('counterpartyLicensePlateNumber', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyLicensePlateNumber',
                                        value
                                    }
                                });
                            }}
                            placeholder="Örn: 34 AB 123"
                            invalid={!!errors.counterpartyLicensePlateNumber || !!(formData.counterpartyLicensePlateNumber && !validateLicensePlate(formData.counterpartyLicensePlateNumber))}
                            disabled={!counterpartyPlateAccess.editable}
                            required={counterpartyPlateAccess.required}
                        />
                        <FormFeedback>{errors.counterpartyLicensePlateNumber || (formData.counterpartyLicensePlateNumber && !validateLicensePlate(formData.counterpartyLicensePlateNumber) ? 'Geçerli bir plaka giriniz (Örn: 34 AB 123).' : '')}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <FormGroup>
                <Label for="counterpartyAddress">Adres {counterpartyAddressAccess.required && <span className="text-danger">*</span>}</Label>
                <Input
                    type="textarea"
                    id="counterpartyAddress"
                    name="counterpartyAddress"
                    value={formData.counterpartyAddress}
                    onChange={handleInputChange}
                    invalid={!!errors.counterpartyAddress}
                    disabled={!counterpartyAddressAccess.editable}
                    required={counterpartyAddressAccess.required}
                />
                <FormFeedback>{errors.counterpartyAddress}</FormFeedback>
            </FormGroup>            <FormGroup>
                <Label for="counterpartyFaultRate">Karşı Taraf Kusur Oranı {counterpartyFaultRateAccess.required && <span className="text-danger">*</span>}</Label>
                <div className="input-group">
                    <Input
                        type="text"
                        id="counterpartyFaultRate"
                        name="counterpartyFaultRate"
                        value={displayDecimal(formData.counterpartyFaultRate || '')}
                        placeholder="Otomatik hesaplanır"
                        invalid={!!errors.counterpartyFaultRate}
                        disabled={true}
                    />
                    <div className="input-group-append">
                        <span className="input-group-text">%</span>
                    </div>
                    <FormFeedback>
                        {errors.counterpartyFaultRate}
                    </FormFeedback>
                </div>
            </FormGroup>

            <FormGroup>
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label">Karşı Taraf Trafik Sigorta Sağlayıcısı {cpTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="counterpartyTrafficInsuranceProvider"
                                name="counterpartyTrafficInsuranceProvider"
                                value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceProvider?.[0] || ""}
                                onChange={handleCounterpartyTrafficInsuranceProviderChange}
                                invalid={!!errors.counterpartyTrafficInsuranceProvider}
                                disabled={!cpTrafficAccess.editable}
                                required={cpTrafficAccess.required}
                            >
                                <option value="">Sigorta Şirketi Seçiniz</option>
                                {insuranceCompanies && insuranceCompanies.length > 0 ?
                                    insuranceCompanies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    )) :
                                    <option value="" disabled>Sigorta şirketi bulunamadı</option>
                                }
                            </Input>
                            <FormFeedback>{errors.counterpartyTrafficInsuranceProvider}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyTrafficInsuranceNumber">Karşı Taraf Trafik Sigorta Numarası {cpTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="counterpartyTrafficInsuranceNumber"
                            name="counterpartyTrafficInsuranceNumber"
                            value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceNumber || ''}
                            onChange={handleCounterpartyTrafficInsuranceNumberChange}
                            invalid={!!errors.counterpartyTrafficInsuranceNumber}
                            disabled={!cpTrafficAccess.editable || cpTrafficIsNoInsurance}
                            required={cpTrafficDetailsRequired}
                        />
                        <FormFeedback>{errors.counterpartyTrafficInsuranceNumber}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyTrafficInsuranceStart">Karşı Taraf Trafik Başlangıç {cpTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="counterpartyTrafficInsuranceStart"
                            name="counterpartyTrafficInsuranceStart"
                            value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart instanceof Date && !isNaN(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart)
                                ? formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyTrafficInsurancePolicy',
                                        value: {
                                            ...formData.counterpartyTrafficInsurancePolicy,
                                            trafficInsuranceStart: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.counterpartyTrafficInsuranceStart}
                            disabled={!cpTrafficStartAccess.editable || cpTrafficIsNoInsurance}
                            required={cpTrafficDetailsRequired}
                        />
                        <FormFeedback>{errors.counterpartyTrafficInsuranceStart}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyTrafficInsuranceExpiry">Karşı Taraf Trafik Sigorta Vade {cpTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="counterpartyTrafficInsuranceExpiry"
                            name="counterpartyTrafficInsuranceExpiry"
                            value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry instanceof Date && !isNaN(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry)
                                ? formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyTrafficInsurancePolicy',
                                        value: {
                                            ...formData.counterpartyTrafficInsurancePolicy,
                                            trafficInsuranceExpiry: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.counterpartyTrafficInsuranceExpiry}
                            disabled={!cpTrafficAccess.editable || cpTrafficIsNoInsurance}
                            required={cpTrafficDetailsRequired}
                        />
                        <FormFeedback>{errors.counterpartyTrafficInsuranceExpiry}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <FormGroup>
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label">Karşı Taraf Kasko Sağlayıcısı {cpCascoAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="counterpartyCascoInsuranceProvider"
                                name="counterpartyCascoInsuranceProvider"
                                value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceProvider?.[0] || ""}
                                onChange={handleCounterpartyCascoInsuranceProviderChange}
                                invalid={!!errors.counterpartyCascoInsuranceProvider}
                                disabled={!cpCascoAccess.editable}
                                required={cpCascoAccess.required}
                            >
                                <option value="">Sigorta Şirketi Seçiniz</option>
                                {insuranceCompanies && insuranceCompanies.length > 0 ?
                                    insuranceCompanies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    )) :
                                    <option value="" disabled>Sigorta şirketi bulunamadı</option>
                                }
                            </Input>
                            <FormFeedback>{errors.counterpartyCascoInsuranceProvider}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyCascoInsuranceNumber">Karşı Taraf Kasko Numarası {cpCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="counterpartyCascoInsuranceNumber"
                            name="counterpartyCascoInsuranceNumber"
                            value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceNumber || ''}
                            onChange={handleCounterpartyCascoInsuranceNumberChange}
                            invalid={!!errors.counterpartyCascoInsuranceNumber}
                            disabled={!cpCascoAccess.editable || cpCascoIsNoInsurance}
                            required={cpCascoDetailsRequired}
                        />
                        <FormFeedback>{errors.counterpartyCascoInsuranceNumber}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyCascoInsuranceStart">Karşı Taraf Kasko Başlangıç {cpCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="counterpartyCascoInsuranceStart"
                            name="counterpartyCascoInsuranceStart"
                            value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart instanceof Date && !isNaN(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart)
                                ? formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyCascoInsurancePolicy',
                                        value: {
                                            ...formData.counterpartyCascoInsurancePolicy,
                                            cascoInsuranceStart: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.counterpartyCascoInsuranceStart}
                            disabled={!cpCascoStartAccess.editable || cpCascoIsNoInsurance}
                            required={cpCascoDetailsRequired}
                        />
                        <FormFeedback>{errors.counterpartyCascoInsuranceStart}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyCascoInsuranceExpiry">Karşı Taraf Kasko Vade {cpCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="counterpartyCascoInsuranceExpiry"
                            name="counterpartyCascoInsuranceExpiry"
                            value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry instanceof Date && !isNaN(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry)
                                ? formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyCascoInsurancePolicy',
                                        value: {
                                            ...formData.counterpartyCascoInsurancePolicy,
                                            cascoInsuranceExpiry: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.counterpartyCascoInsuranceExpiry}
                            disabled={!cpCascoAccess.editable || cpCascoIsNoInsurance}
                            required={cpCascoDetailsRequired}
                        />
                        <FormFeedback>{errors.counterpartyCascoInsuranceExpiry}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <div style={{ height: "20px" }}></div>
            <hr className="mt-4 mb-4" />
            <div style={{ height: "20px" }}></div>

            <Col>
                <div className='m-t-15 m-checkbox-inline custom-radio-ml'>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-1' type='checkbox' />
                        <Label for='inline-1'>
                            Resim
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-2' type='checkbox' />
                        <Label for='inline-2'>
                            KM
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-3' type='checkbox' />
                        <Label for='inline-3'>
                            Ruhsat
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-4' type='checkbox' />
                        <Label for='inline-4'>
                            KTT
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-5' type='checkbox' />
                        <Label for='inline-5'>
                            Poliçe
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-6' type='checkbox' />
                        <Label for='inline-6'>
                            Fatura
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-7' type='checkbox' />
                        <Label for='inline-7'>
                            Eksper Raporu
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-8' type='checkbox' />
                        <Label for='inline-8'>
                            Kusur Durumu Gösteri Belgesi
                        </Label>
                    </div>
                </div>
            </Col>
        </>
    );
};

DocumentRequestForm.propTypes = {
    formData: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFormData: PropTypes.func.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    handleCounterPartyTypeChange: PropTypes.func.isRequired,
    insuranceCompanies: PropTypes.array.isRequired,
    fieldPermissions: PropTypes.array,
    canEdit: PropTypes.bool,
};

export default DocumentRequestForm;
